import { getBoundValue } from "@odata/Data.utils";
import { TFunction } from "i18next";
import { cloneDeep, trim } from "lodash";

import { FieldType } from "../../../enums";
import { TRecordType, TValue } from "../../../global.types";
import { TableStorage } from "../../../model/TableStorage";
import BindingContext, { IEntity } from "../../../odata/BindingContext";
import { IFormStorageDefaultCustomData } from "../../../views/formView/FormStorage";
import { IFieldDef } from "../FieldInfo";


export enum BulkChangeAction {
    Add = "Add",
    Append = "Append",
    Prepend = "Prepend",
    Replace = "Replace",
    SetForEmptyValues = "SetForEmptyValues",
    Remove = "Remove",
    RemoveAll = "RemoveAll",
    RemoveExact = "RemoveExact"
}

export interface IBulkChangeDialogStorageCustomData extends IFormStorageDefaultCustomData {
    isChecked: Record<string, boolean>;
}

// todo could be changed from PerFieldType to differentiate between collections and single entities,
// we'll see after we use more than two different fields in BulkChange
export const BulkChangeActionPerFieldType: Partial<Record<FieldType, BulkChangeAction[]>> = {
    [FieldType.LabelSelect]: [BulkChangeAction.Add, BulkChangeAction.Replace, BulkChangeAction.RemoveExact, BulkChangeAction.RemoveAll],
    [FieldType.EditableText]: [BulkChangeAction.Append, BulkChangeAction.Prepend, BulkChangeAction.Replace, BulkChangeAction.RemoveAll],
    [FieldType.ComboBox]: [BulkChangeAction.SetForEmptyValues, BulkChangeAction.Replace, BulkChangeAction.RemoveExact, BulkChangeAction.RemoveAll]
};

export const getBulkChangeActionTranslation = (id: BulkChangeAction, isCollection: boolean, t: TFunction, fieldType?: FieldType): string => {
    const useRemoveExisting = id === BulkChangeAction.RemoveAll && !isCollection && fieldType !== FieldType.ComboBox;
    return t(`Common:BulkChange.${useRemoveExisting ? "RemoveExisting" : id}`);
};

export const getDefaultBulkChangeAction = (isCollection: boolean): BulkChangeAction => {
    return isCollection ? BulkChangeAction.Add : BulkChangeAction.Replace;
};

export function replaceComplexActionValues(storage: TableStorage, data: IEntity, originalEntity: IEntity, columns: string[], collectionActions: TRecordType<BulkChangeAction>): IEntity {
    const replaced = cloneDeep(data);
    columns.forEach(path => {
        const bindingContext = storage.data.bindingContext.navigate(path);
        const def = { id: path, ...storage.data.definition.massEditableDef[path] };
        replaced[path] = replaceComplexActionValue(def, data, originalEntity, collectionActions[path], bindingContext);
    });
    return replaced;
}

export function replaceComplexActionValue(def: IFieldDef, data: IEntity, originalEntity: IEntity, action: BulkChangeAction, bindingContext: BindingContext): TValue[] | TValue {
    const path = def.id;
    const isCollection = bindingContext.isCollection();
    const origCollection = originalEntity[path] ?? [];
    const getKeyPath = () => def.fieldSettings?.keyPath;
    const getKeyPathBc = () => bindingContext.navigate(getKeyPath() ?? bindingContext.getKeyPropertyName());


    // compares collection items by key property
    const _areItemsSame = (item1: IEntity, item2: IEntity): boolean => {
        const id1 = getBoundValue({ bindingContext: getKeyPathBc(), data: item1, dataBindingContext: bindingContext });
        const id2 = getBoundValue({ bindingContext: getKeyPathBc(), data: item2, dataBindingContext: bindingContext });
        return id1 === id2;
    };

    const _isItemInCollection = (item: IEntity, collection: IEntity[]) => {
        return collection.find(collectionItem => _areItemsSame(collectionItem, item));
    };

    switch (action) {
        case BulkChangeAction.Prepend:
            return trim(`${data[path]} ${originalEntity[path] ?? ""}`);
        case BulkChangeAction.Append:
            return trim(`${originalEntity[path] ?? ""} ${data[path]}`);
        case BulkChangeAction.RemoveAll:
            return isCollection ? [] : null;
        case BulkChangeAction.Replace:
        case BulkChangeAction.SetForEmptyValues:
            return data[path];
        case BulkChangeAction.Remove:
            // new value is original value without removed items
            return origCollection.filter((originalItem: IEntity) => !_isItemInCollection(originalItem, data[path]));
        case BulkChangeAction.Add:
        default:
            // new value is original value with added items, if they are not already contained
            const addedWithoutOriginal = (data[path] ?? []).filter(
                (addedItem: IEntity) => !_isItemInCollection(addedItem, origCollection)
            );
            return [...origCollection, ...addedWithoutOriginal];
    }
}